import { ButtonBase, ClickAwayListener, Fade, Paper, Popper, Skeleton } from "@mui/material";
import { alpha } from "@mui/material";
import Stack from "atoms/Stack";
import WithTooltip from "atoms/WithTooltip";
import { MAX_CONTENT_WIDTH } from "helpers/breakpoints";
import { COLOR_BLACK, COLOR_GREY_BORDER, COLOR_WHITE, COLOR_WHITE_LIGHTER } from "helpers/colors";
import { FONT_FAMILY_GRAPHIK } from "helpers/FONT";
import { SPACING, SPACING_DENSE, SPACING_DENSE_HALF, SPACING_HALF } from "helpers/spacings";
import usePseudoStyle from "helpers/usePseudoStyle";
import { defer } from "lodash-es";
import { Check, MenuDown } from "mdi-material-ui";
import React, { createContext, useContext, useId, useRef, useState } from "react";
import { useComponentSize } from "react-use-size";

const subnavContext = createContext();
const subnavDropdownContext = createContext({});

export default function Subnav({ children, secondary = false, transparent = false, wrap = true }) {
  const componentSize = useComponentSize();

  return (
    <subnavContext.Provider value={{ secondary, transparent, componentSize }}>
      <Stack
        horizontal
        padding
        componentRef={componentSize.ref}
        dense
        noWrap={!wrap}
        data-test-subnav
        style={{
          minHeight: 56,
          color: COLOR_BLACK,
          maxWidth: MAX_CONTENT_WIDTH,
          width: `calc(100vw - ${SPACING} * 2)`,
          backgroundColor: COLOR_WHITE_LIGHTER,
          overflowX: wrap ? undefined : "auto",
          ...(transparent && {
            backgroundColor: "transparent",
          }),
        }}
      >
        {children}
      </Stack>
    </subnavContext.Provider>
  );
}

export function SubnavToggleLabel({ icon, endIcon, text, tooltip, checked = false, disabled = false, ...others }) {
  const { className } = usePseudoStyle({
    ":hover": `color: ${COLOR_BLACK} !important; background-color: ${COLOR_WHITE} !important;`,
  });

  return (
    <WithTooltip
      tooltip={tooltip}
      element={
        <ButtonBase
          className={className}
          style={{
            paddingBottom: 0,
            paddingLeft: SPACING_DENSE,
            paddingRight: SPACING_DENSE,
            border: `solid 1px ${COLOR_GREY_BORDER}`,
            fontFamily: FONT_FAMILY_GRAPHIK,
            marginTop: 13,
            borderRadius: 15,
            height: 24,
            fontSize: 12,
            color: COLOR_BLACK,
            fontWeight: checked ? 500 : 400,
            backgroundColor: checked ? COLOR_WHITE : "transparent",
            borderColor: checked ? `${alpha(COLOR_BLACK, 0.4)}` : `${COLOR_GREY_BORDER}`,
            ...(disabled && {
              opacity: 0.5,
            }),
            ...others,
          }}
          {...others}
          disabled={disabled}
        >
          {icon}
          {text}
          {endIcon}
        </ButtonBase>
      }
    />
  );
}

export function SubnavDropdown({ icon, text, children, endIcon }) {
  const popperId = useId();
  const ref = useRef(null);
  const [open, openSet] = useState(false);
  const { transparent } = useContext(subnavContext);

  return (
    <subnavDropdownContext.Provider value={{ openSet }}>
      <ButtonBase
        ref={ref}
        color="inherit"
        href="#"
        onClick={() => openSet(true)}
        style={{
          margin: SPACING_HALF,
          padding: SPACING_HALF,
          fontSize: "0.7em",
          height: 32,
          fontFamily: FONT_FAMILY_GRAPHIK,
          fontWeight: 500,
          gap: SPACING_DENSE,
          border: `solid 1px ${COLOR_GREY_BORDER}`,
          borderRadius: 12,
        }}
      >
        {icon}
        {text && (
          <span
            style={{
              maxWidth: 240,
              display: "inline-block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {text}
          </span>
        )}
        {!text && <Skeleton style={{ backgroundColor: "#aaa", width: 80 }} />}
        {endIcon}
        <MenuDown
          style={{
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease-out",
          }}
        />
      </ButtonBase>
      <Popper
        id={popperId}
        anchorEl={ref.current}
        open={open}
        placement="bottom-start"
        transition
        style={{ zIndex: 1 }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={200}>
            <div>
              <ClickAwayListener onClickAway={() => openSet(false)}>
                <Paper
                  data-test-subnav-dropdown
                  square
                  style={{
                    backgroundColor: COLOR_WHITE_LIGHTER,
                    color: COLOR_BLACK,
                    border: `solid 1px ${alpha(COLOR_BLACK, 0.2)}`,
                    borderRadius: 12,
                    boxShadow: `0px 4px 12px ${alpha(COLOR_BLACK, 0.2)}`,
                    minWidth: 220,
                    display: "flex",
                    flexFlow: "column nowrap",
                    ...(transparent && {
                      backgroundColor: COLOR_WHITE,
                      color: COLOR_BLACK,
                    }),
                  }}
                  {...(transparent && {
                    square: false,
                  })}
                >
                  {children}
                </Paper>
              </ClickAwayListener>
            </div>
          </Fade>
        )}
      </Popper>
    </subnavDropdownContext.Provider>
  );
}

export function SubnavDropdownSearchField({ searchTerm, searchTermSet }) {
  return (
    <input
      type="search"
      autoFocus
      style={{
        border: `1px solid ${COLOR_GREY_BORDER}`,
        borderRadius: 12,
        padding: SPACING_DENSE,
        margin: SPACING_DENSE,
        fontSize: "0.75em",
        fontFamily: FONT_FAMILY_GRAPHIK,
        backgroundColor: COLOR_WHITE_LIGHTER,
        color: COLOR_BLACK,
      }}
      value={searchTerm || ""}
      placeholder="Search..."
      onChange={(event) => {
        searchTermSet(event.target.value);
      }}
    />
  );
}

export function SubnavDropdownItem({ icon, text, description, selected = false, href, onClick }) {
  const [hovering, hoveringSet] = useState(false);
  const { openSet } = useContext(subnavDropdownContext);
  const selectedColor = "#e0137a";
  const selectedBackground = "rgb(254,246,247)";
  const hoverBackground = "#f5f5f5";

  return (
    <ButtonBase
      onMouseEnter={() => hoveringSet(true)}
      onMouseLeave={() => hoveringSet(false)}
      style={{
        display: "flex",
        flexFlow: "row nowrap",
        justifyContent: "space-between",
        alignItems: "center",
        gap: SPACING_DENSE,
        padding: SPACING_DENSE,
        fontSize: "0.75em",
        fontFamily: FONT_FAMILY_GRAPHIK,
        borderRadius: 12,
        minHeight: 48,
        backgroundColor: selected ? selectedBackground : hovering ? hoverBackground : "white",
        color: selected ? selectedColor : COLOR_BLACK,
        transition: "background-color 0.15s ease, color 0.15s ease",
      }}
      href={href || "#"}
      onClick={(event) => {
        defer(() => openSet?.(false));
        onClick?.(event);
      }}
    >
      <div
        style={{
          display: "flex",
          flexFlow: "row nowrap",
          gap: SPACING_DENSE,
          alignItems: "center",
          flex: "1 1 auto",
        }}
      >
        {icon}
        <div
          style={{
            display: "flex",
            flexFlow: "column nowrap",
            maxWidth: 300,
          }}
        >
          <div
            style={{
              fontWeight: 500,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {text}
          </div>
          <div
            style={{
              opacity: 0.7,
              fontSize: "0.85em",
              paddingTop: SPACING_DENSE_HALF,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {description}
          </div>
        </div>
      </div>
      {selected && (
        <span
          style={{
            color: selectedColor,
            fontSize: "1em",
            fontWeight: 500,
          }}
        >
          <Check fontSize="inherit" />
        </span>
      )}
    </ButtonBase>
  );
}

export function SubnavIcon({ src, alt = "Icon" }) {
  if (!src) return <Skeleton variant="circular" style={{ width: 20, height: 20, backgroundColor: "#aaa" }} />;
  return <img src={src} alt={alt} style={{ width: 20, aspectRatio: "1/1", objectFit: "contain" }} />;
}
